import { Middleware } from '@reduxjs/toolkit';
import { clearFilters, setFilters } from '../filtersSlice';
import { hideColumns, showAllColumns, selectAllColumns } from '../columnsSlice';
import { setGroupBy } from '../groupBySlice';
import { setSorting } from '../sortingSlice';
import { updateView } from '../viewsSlice';
import { selectIsFiltersControlled } from '../controlledFiltersSlice';
import { FilterState, FolderView, GroupState, ItemView, SortState, View, Views } from '../../../types/Spreadsheet';


function findViewById( views: Views, id: string ): View | undefined {
  for ( const view of views ) {
    if ( view.type === 'folder' ) {
      const found = ( view as FolderView ).children?.length ? findViewById(( view as FolderView ).children, id ) : undefined;
      if ( found ) return found;
    } else if ( view.type === 'view' ) {
      if ( view.id === id ) return view;
    }
  }
  return undefined;
}

// Actions that modify spreadsheet state and should update active view
const SPREADSHEET_STATE_ACTIONS = [
  'filters/setFilters',
  'filters/addFilter',
  'filters/removeFilter',
  'filters/updateFilter',
  'filters/clearFilters',
  'sorting/setSorting',
  'groupBy/setGroupBy',
  'columns/hideColumns',
  'columns/showColumns',
  'columns/showAllColumns',
  'columns/setColumns',
  'columns/updateColumn'
];

export const activeViewMiddleware: Middleware<{}, any> = store => {
  let prevActiveViewId = store.getState().spreadsheet.views.activeViewId;
  let isApplyingView = false;

  return next => action => {
    // Capture previous state for comparison
    const prevState = store.getState();
    const prevHiddenColumns = selectAllColumns( prevState )
      .filter(( col: any ) => col.hidden )
      .map(( col: any ) => col.id )
      .sort()
      .join( ',' );

    const result = next( action );
    
    const state = store.getState();
    const newActiveViewId = state.spreadsheet.views.activeViewId;

    // When active view changes, apply its settings to spreadsheet
    if ( newActiveViewId !== prevActiveViewId ) {
      prevActiveViewId = newActiveViewId;
      const activeView = newActiveViewId
        ? findViewById( state.spreadsheet.views.views, newActiveViewId )
        : undefined;

      if ( activeView && activeView.type === 'view' ) {
        isApplyingView = true;

        // Check if filters are controlled externally
        const isFiltersControlled = selectIsFiltersControlled( state );

        // Only clear/apply filters if NOT controlled
        if ( !isFiltersControlled ) {
          store.dispatch( clearFilters());
          if (( activeView as ItemView )?.filters?.length ) {
            store.dispatch( setFilters(( activeView as ItemView ).filters as FilterState ));
          }
        }

        // Always apply other view settings
        store.dispatch( showAllColumns());
        store.dispatch( setGroupBy( null ));
        store.dispatch( setSorting( null ));
        if (( activeView as ItemView ).groupBy ) {
          store.dispatch( setGroupBy(( activeView as ItemView ).groupBy as GroupState ));
        }
        if (( activeView as ItemView )?.hideColumns?.length ) {
          store.dispatch( hideColumns(( activeView as ItemView ).hideColumns as string[]));
        }
        if (( activeView as ItemView ).sort ) {
          store.dispatch( setSorting(( activeView as ItemView ).sort as SortState ));
        }
        isApplyingView = false;
      }
    }

    // When spreadsheet state changes, update active view (reverse sync)
    if ( !isApplyingView && SPREADSHEET_STATE_ACTIONS.includes(( action as { type: string }).type ) && newActiveViewId ) {
      // Note: We allow filter updates to views even in controlled mode
      // The controlled mode only prevents forward sync (view → spreadsheet)
      // but allows reverse sync (spreadsheet → view) for persistence

      // For setColumns, only proceed if hidden columns actually changed
      let shouldUpdate = true;
      if (( action as { type: string }).type === 'columns/setColumns' ) {
        const nextHiddenColumns = selectAllColumns( state )
          .filter(( col: any ) => col.hidden )
          .map(( col: any ) => col.id )
          .sort()
          .join( ',' );
        if ( prevHiddenColumns === nextHiddenColumns ) {
          shouldUpdate = false;
        }
      }

      if ( shouldUpdate ) {
        const activeView = findViewById( state.spreadsheet.views.views, newActiveViewId );

        if ( activeView && activeView.type === 'view' ) {
          // Get current spreadsheet state
          const currentFilters = state.spreadsheet.filters.filters;
          const currentSorting = state.spreadsheet.sorting.sorting;
          const currentGroupBy = state.spreadsheet.groupBy.groupBy;
          const currentHiddenColumns = selectAllColumns( state )
            .filter(( col: any ) => col.hidden )
            .map(( col: any ) => col.id );

          // Update active view with current spreadsheet state
          // Always include filters for persistence (controlled mode only affects forward sync)
          store.dispatch( updateView({
            id: newActiveViewId,
            changes: {
              filters: currentFilters.length > 0 ? currentFilters : undefined,
              sort: currentSorting,
              groupBy: currentGroupBy,
              hideColumns: currentHiddenColumns.length > 0 ? currentHiddenColumns : undefined
            }
          }));
        }
      }
    }

    return result;
  };
};
